import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  getPollVoteSessionOwner,
  isAnonymousVoteOwner,
  isUnverifiedVoter,
  savePollVoteLocally
} from "@/components/quick-poll/logic";
import { getQuickPoll, patchQuickPoll, setQuickPoll } from "@/models";
import { AppState } from "@/redux/store/store";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";

const isAnonymousFromState = (state: AppState): boolean => {
  const { registeredUser, type } = state.user;
  return isAnonymousVoteOwner(
    getPollVoteSessionOwner({
      isRegisteredUserLoggedIn: !!registeredUser?.isLoggedIn,
      userId: registeredUser?.profile?.id,
      userType: type
    })
  );
};

export const fetchQuickPoll = createAsyncThunk(
  "quickPoll/fetchQuickPoll",
  async (
    params: { projectId: string; quickPollId: string; config?: object },
    { rejectWithValue, getState }
  ) => {
    const { adapter } = getState() as AppState;
    const { baseUrl } = adapter.adapter;
    const { projectId, quickPollId, config } = params;
    const { token } = fetchAuthToken(getState());

    try {
      return await getQuickPoll(token, baseUrl, projectId, quickPollId, config);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const saveQuickPoll = createAsyncThunk(
  "quickPoll/saveQuickPoll",
  async (
    params: {
      projectId: string;
      quickPollId: string;
      /** Widget polls pass their own answer so lists do not share the slice. */
      answer?: { optionId: null | string | number };
      userResponseId?: string | number | null;
    },
    { rejectWithValue, getState }
  ) => {
    const state = getState() as AppState;
    const { quickPoll } = state.quickPoll;
    const answer = params.answer ?? state.quickPoll.answer;
    const { baseUrl } = state.adapter.adapter;
    const { projectId, quickPollId } = params;
    const { token } = fetchAuthToken(getState());
    const isAnonymous = isAnonymousFromState(state);
    const unverifiedVoter = isUnverifiedVoter(
      !!state.user.unverifiedUser?.token,
      !!state.user.registeredUser?.isLoggedIn
    );
    const responseId =
      params.userResponseId !== undefined
        ? params.userResponseId
        : quickPoll.userResponseId;

    try {
      // Session + existing response → PATCH; otherwise POST (anon re-POST upserts).
      const response =
        !isAnonymous && responseId
          ? await patchQuickPoll(
              token,
              baseUrl,
              projectId,
              quickPollId,
              responseId,
              answer
            )
          : await setQuickPoll(token, baseUrl, projectId, quickPollId, answer);

      if (isAnonymous && !unverifiedVoter && answer.optionId != null) {
        savePollVoteLocally(projectId, quickPollId, answer.optionId);
      }

      return {
        optionId: answer.optionId,
        userResponseId: response?.id ?? responseId ?? null
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
